/*
 * We use "lgu-*" ("legacy unit") prefix for legacy size variants
 * (prefix chosen to be both short and easily greppable).
 * The app used to have rem set to 10px, making tailwind's 1/4 fractions of rem subpixel.
 * To migrate to rem=16px and avoid restyling whole app, we introduced these prefixes for legacy styles.
 * PLEASE DON'T USE THESE LEGACY VARIANTS IN NEW CODE/COMPONENTS.
 * Feel free to remove each legacy variant when it is not used anymore.
 */

import type { CustomThemeConfig } from 'tailwindcss/types/config';
import { figmaTailwindTheme } from './figmaTailwindTheme';

/** Our custom theme override of default Tailwind theme */
export const ipfTheme = {
  ...figmaTailwindTheme,
  extend: {
    backgroundImage: {
      // an additional background color layer to represent interaction states, e.g. hover, press, faded
      'state-faded':
        'linear-gradient(0deg, var(--semantic-colors-tint-tint-40) 0%, var(--semantic-colors-tint-tint-40) 100%)',
      'state-hover':
        'linear-gradient(0deg, var(--semantic-colors-interaction-hover) 0%, var(--semantic-colors-interaction-hover) 100%)',
      'state-hover-inverted':
        'linear-gradient(0deg, var(--semantic-colors-interaction-hover-inverted) 0%, var(--semantic-colors-interaction-hover-inverted) 100%)',
      'state-press':
        'linear-gradient(0deg, var(--semantic-colors-interaction-press) 0%, var(--semantic-colors-interaction-press) 100%)',
      'state-press-inverted':
        'linear-gradient(0deg, var(--semantic-colors-interaction-press-inverted) 0%, var(--semantic-colors-interaction-press-inverted) 100%)',
      'state-drop-shadow':
        'linear-gradient(0deg, var(--semantic-colors-interaction-drop-shadow) 0%, var(--semantic-colors-interaction-drop-shadow) 100%)',
      'state-overlay':
        'linear-gradient(0deg, var(--semantic-colors-interaction-overlay) 0%, var(--semantic-colors-interaction-overlay) 100%)',
    },
    borderColor: ({ theme }) => ({
      DEFAULT: theme('colors.gray.300', 'currentColor'),
    }),
    borderWidth: {
      0: '0',
      2: '2px',
      4: '4px',
      8: '8px',
    },
    boxShadow: {
      // box shadow simulating the bottom border of the element without effect on layout
      'bottom-border-default': `inset 0 -1px 0 0 theme('borderColor.default')`,
      'bottom-border-selected': `inset 0 -1px 0 0 theme('borderColor.status-selected')`,
      'bottom-border-disabled': `inset 0 -1px 0 0 theme('borderColor.status-disabled-dark')`,
      'bottom-border-lg-default': `inset 0 -2px 0 0 theme('borderColor.default')`,
      'bottom-border-lg-selected': `inset 0 -2px 0 0 theme('borderColor.status-selected')`,
      'bottom-border-lg-disabled': `inset 0 -2px 0 0 theme('borderColor.status-disabled-dark')`,
      // asymmetric 3-sided focus ring specific for tabs 3-sided focus ring specific for tabs
      'tab-ring': `inset 0 2px 0 0 theme('borderColor.status-focused'), inset 2px 0 0 0 theme('borderColor.status-focused'), inset -2px 0 0 0 theme('borderColor.status-focused')`,
    },
    colors: {
      inherit: 'inherit',
      current: 'currentColor',
      transparent: 'transparent',

      white: 'rgb(var(--base-colors-white-white100) / <alpha-value>)',
      black: 'rgb(var(--base-colors-black-black100) / <alpha-value>)',

      // IPF logo colors are fixed to remain unaffected by future changes in Design Tokens Figma
      logo: {
        'color-brand': '#264183',
        'color-gray': '#8C989B',
        white: '#FFFFFF',
        black: '#000000',
        // ... except for shade color variant, which should reflect active theme mode (light/dark) though
        shade: 'var(--semantic-colors-shade-shade-20)',
      },

      // brand colors
      'spanish-gray': '#8c989b',
      'generic-viridian': '#148567',
      gunmetal: '#222d32',
      'dark-cornflower-blue': '#264183',
      platinum: '#e5e5e5',
      cultured: '#f6f6f6',
      'link-legacy': '#4559a6',

      // palette colors
      darkgray: {
        500: '#667085',
        600: '#475467',
        700: '#344054',
        800: '#1d2939',
      },
      gray: {
        50: '#f5f5f5',
        100: '#eeeced',
        200: '#e5e7eb',
        300: '#e8e8e8',
        400: '#9ca3af',
        600: '#d6d6d6',
        700: '#c4c4c4',
        800: '#8c8c8c',
        900: '#323232',
      },
      bluegray: {
        50: '#f8f9fc',
        100: '#eaecf5',
        200: '#c8cce5',
        400: '#333333',
        500: '#667085',
        600: '#475467',
      },
      blue: {
        50: '#ebf5ff',
        100: '#e1effe',
        200: '#cce5ff',
        300: '#99ccff',
        400: '#76a9fa',
        500: '#3f83f8',
        600: '#1c64f2',
        700: '#1a56db',
        800: '#2693ff',
        900: '#4F81BD',
        925: '#4559a6',
        950: '#1b2d5b',
      },
      green: {
        50: '#f3faf7',
        100: '#def7ec',
        200: '#bcf0da',
        300: '#84e1bc',
        400: '#44bb85',
        500: '#0e9f6e',
        600: '#0D8F54',
        700: '#046c4e',
        800: '#6fb96f',
        900: '#497349',
      },
      red: {
        50: '#fdf2f2',
        100: '#fde8e8',
        200: '#fbd5d5',
        300: '#f8b4b4',
        350: '#f98080',
        400: '#ff6666',
        500: '#f05252',
        600: '#e02424',
        700: '#b14c4b',
        800: '#9b1c1c',
        900: '#771d1d',
      },
      orange: {
        50: '#fff6ed',
        100: '#ffead5',
        200: '#fddcab',
        300: '#feb273',
        400: '#ff9800',
        500: '#fb6514',
        600: '#ec4a0a',
        700: '#c4320a',
        800: '#b87108',
        900: '#7e2410',
      },

      // semantic colors for severity bound to DS palette
      severity: {
        0: 'rgb(var(--semantic-colors-surface-status-success) / <alpha-value>)',
        10: 'rgb(var(--semantic-colors-surface-status-info) / <alpha-value>)',
        20: 'rgb(var(--semantic-colors-surface-status-warning) / <alpha-value>)',
        30: 'rgb(var(--semantic-colors-surface-status-error) / <alpha-value>)',
      },

      warning:
        'rgb(var(--semantic-colors-surface-status-warning) / <alpha-value>)',
    },
    content: {
      // empty content for pseudo-elements :after, :before
      empty: '""',
    },
    dropShadow: {
      // drop-shadow filter matching boxShadow.DEFAULT from figmaTailwindTheme.ts
      // Note: drop-shadow doesn't support spread-radius, so the 1px spread from the original
      // box-shadow (0px 2px 4px 1px #00000033) is approximated without spread
      // Usage: class="drop-shadow" (Tailwind will generate: filter: drop-shadow(0px 2px 4px #00000033))
      DEFAULT: '0px 2px 4px #00000033',
    },
    flex: {
      // https://bugs.chromium.org/p/chromium/issues/detail?id=495306#c3
      // original flex-1 from tailwind gives flex-basis: 0%, but when parent
      // has computed rather than explicit height (e.g. it is a flex element itself),
      // 0% works as 'auto', so it doesn't fit to available space as expected.
      1: '1 1 0',
      2: '2 1 0',
      fixed: '0 0 auto',
    },
    fontFamily: {
      sans: [
        ...figmaTailwindTheme.fontFamily.sans,
        '"Helvetica Neue"',
        'Helvetica',
        'Arial',
        'sans-serif',
      ],
      heading: [
        ...figmaTailwindTheme.fontFamily.heading,
        '"Helvetica Neue"',
        'Helvetica',
        'Arial',
        'sans-serif',
      ],
      mono: [
        // Tailwind 3 default
        'ui-monospace',
        'SFMono-Regular',
        'Menlo',
        'Monaco',
        'Consolas',
        'Liberation Mono',
        'Courier New',
        'monospace',
      ],
    },
    fontSize: {
      // legacy font sizes
      xxs: '0.5rem',
      xl: ['1.25rem', '1.75rem'],
      '2xl': ['1.5rem', '2rem'],
      '3xl': ['1.875rem', '2.25rem'],
      '4xl': ['2.25rem', '2.5rem'],
      '5xl': ['3rem', '3rem'],
      '6xl': ['3.75rem', '3.75rem'],
      '7xl': ['4.5rem', '4.5rem'],
      '8xl': ['6rem', '6rem'],
      '9xl': ['8rem', '8rem'],
      'lgu-base': '10px',
      'lgu-lg': '11.25px',
      'lgu-xl': '12.5px',
      'lgu-2xl': '15px',
      'lgu-3xl': '18.75px',
    },
    fontWeight: {
      // 'normal' is just an alias for 'regular' font weight in Design System, to be compatible with Tailwind CSS documentation
      normal: 'var(--base-font-weight-regular)',
    },
    height: {
      6.5: '1.625rem',
    },
    inset: {
      0: '0',
    },
    lineHeight: {
      'lgu-5': '12.5px',
      'lgu-6': '15px',
      'lgu-7': '17.5px',
    },
    margin: ({ theme }) => ({
      auto: 'auto',
      inherit: 'inherit',
      ...theme('spacing'),
    }),
    maxWidth: {
      'content-default-legacy': '54rem',
    },
    minHeight: {
      40: '40px',
    },
    padding: ({ theme }) => theme('spacing'),
    gap: ({ theme }) => theme('spacing'),
    ringOpacity: {
      DEFAULT: '1',
    },
    ringWidth: {
      2: '2px',
    },
    screens: {
      sx: '480px',
      'max-sm': { max: '767px' },
      sm: '768px',
      md: '992px',
      lg: '1200px',
      xl: '1408px',
      xxl: '1600px',
    },
    spacing: {
      px: '1px',
      5.5: '1.375rem',
      13: '3.25rem',
      24: '6rem',
      28: '7rem',
      32: '8rem',
      36: '9rem',
      40: '10rem',
      44: '11rem',
      48: '12rem',
      52: '13rem',
      56: '14rem',
      60: '15rem',
      64: '16rem',
      72: '18rem',
      80: '20rem',
      96: '24rem',
      'lgu-1': '2.5px',
      'lgu-2': '5px',
      'lgu-3': '7.5px',
      'lgu-5': '12.5px',
      'lgu-6': '15px',
      'lgu-10': '25px',
      'lgu-12': '30px',
      'lgu-16': '40px',
      'lgu-20': '50px',
      'lgu-24': '60px',
      'lgu-64': '160px',
    },
    width: {
      // default content (page, form etc) that should not stretch to full width in large window
      'content-default-legacy': '54rem',
    },
    /*
     * Please keep in mind that we unfortunately have plenty of z-index definitions
     * directly in styles without tailwind. When you change some number here,
     * keep in mind it can break ordering with elements z-indexed aside of tailwind.
     */
    zIndex: {
      behind: '-1',
      elevated: '1',
      dropdown: '2',
      popup: '10',
      // PageHeader as fixed top bar must be rendered above all content, but below navigation.
      pageHeader: '500',
      // Left menu navigation is rendered above content because of shadow and button to expand/collapse the nav.
      navigation: '850',
      modal: '1300',
      // Dropdown in FloatingPortal must be rendered with a higher z-index than modal.
      // We assume that all dropdowns should be closed when a modal is opened, and in legitimate situations
      // no dropdown should remain open "behind" a modal.
      floatingDropdown: '1400',
      tooltip: '1500',
      dragHandleBackdrop: '1999',
      dragHandle: '2000',
      // Overlay when app is unavailable (maintenance or server down) is rendered above everything.
      siteDown: '99999',
      floatingDebugTool: '150000',
    },
  },
} satisfies Partial<CustomThemeConfig & { extend: Partial<CustomThemeConfig> }>;
